import LegalDocument, { type LegalSection } from '@/components/LegalDocument';

/**
 * Privacy Policy.
 *
 * Kept in the app (rather than a static file) so it is reachable without a
 * marketing site and can never drift from the product it describes. The copy
 * deliberately spells out that payments become credits and that credits are not
 * money — that is the single most misunderstood part of the billing model.
 */
const UPDATED = '4 October 2026';
const CONTACT_EMAIL = 'privacy@aimedia.example';

const SECTIONS: LegalSection[] = [
  {
    id: 'who-we-are',
    heading: '1. Who we are',
    body: (
      <>
        <p>
          Social Media Autopilot ("we", "us") operates a social media autopilot: it connects to your social
          accounts, generates content with AI, and schedules and publishes posts on your behalf.
          This policy explains what personal data we handle, why we handle it, and the choices
          you have.
        </p>
        <p>
          We act as a <strong>data fiduciary</strong> for our users' personal data. Where we
          process personal data on behalf of a business customer, that customer is the data
          fiduciary and we act as an information processor under a written agreement with them.
        </p>
        <p>
          For any privacy question, correction or complaint, contact us at{' '}
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
        </p>
      </>
    ),
  },
  {
    id: 'information-we-collect',
    heading: '2. Information we collect',
    body: (
      <>
        <h6>2.1 Information you give us</h6>
        <ul>
          <li>
            <strong>Account data</strong> — email address, display name, password (stored only
            as a bcrypt hash) and timezone.
          </li>
          <li>
            <strong>Content</strong> — the drafts, posts, campaign briefs, images, videos and
            captions you create or generate through the service.
          </li>
          <li><strong>Support messages</strong> — anything you send us by email or in-app.</li>
        </ul>

        <h6>2.2 Information collected automatically</h6>
        <ul>
          <li>
            <strong>Connected social accounts</strong> — platform, account name, external
            identifiers, follower counts, and the access and refresh tokens issued by the
            platform. Tokens are encrypted at rest with AES-256-GCM and never returned to your
            browser in readable form.
          </li>
          <li>
            <strong>Analytics</strong> — impressions, reach, engagements, clicks and video views
            retrieved from the platforms for your posts.
          </li>
          <li>
            <strong>Technical data</strong> — IP address, device and browser type, request logs
            with timestamps, and audit records of administrative actions.
          </li>
          <li>
            <strong>Usage data</strong> — how often each feature is used, used to enforce plan
            limits and understand product performance.
          </li>
        </ul>

        <h6>2.3 Payment data</h6>
        <p>
          Payments are processed by <strong>Razorpay</strong>, an independent payment gateway.
          Card numbers, UPI credentials and CVVs are entered on Razorpay's hosted page and{' '}
          <strong>never reach our servers</strong>. We do not see, store or process your card or
          UPI details.
        </p>
        <p>We retain only what is needed to administer the purchase:</p>
        <ul>
          <li>the payment status, timestamp and provider reference;</li>
          <li>the plan purchased and the credits it converted into;</li>
          <li>the amount charged, in paise;</li>
          <li>the method used (UPI or card) — never the underlying instrument.</li>
        </ul>
        <p>
          Razorpay processes this data under its own privacy policy and is an independent
          controller for the payment data it collects. We never receive your full card number or
          UPI PIN.
        </p>
      </>
    ),
  },
  {
    id: 'credits-and-referrals',
    heading: '3. Credits, referrals, and the fact that credits are not money',
    body: (
      <>
        <p>
          <strong>
            Your balance is denominated in credits, not money. Any payment you make is converted
            into credits.
          </strong>{' '}
          When you pay for a plan over UPI or card, the money is settled by the payment gateway
          and your account is credited with that plan's credits. Credits are a promotional unit
          of account inside the service.
        </p>

        <h6>3.1 Credits cannot be withdrawn</h6>
        <p>Specifically, and notwithstanding anything else in these documents:</p>
        <ul>
          <li>
            <strong>Credits are not money.</strong> They are not currency, not a deposit, not a
            stored balance and not a prepaid payment instrument. They have no cash value outside
            the service.
          </li>
          <li>
            <strong>Credits cannot be withdrawn, redeemed or cashed out.</strong> No feature,
            request or support process converts credits into money, and no circumstance —
            including account closure or termination — obliges us to do so.
          </li>
          <li>
            <strong>Credits are not transferable.</strong> They cannot be sold, gifted, traded,
            bartered or passed to another account, and no account may be resold or transferred.
          </li>
          <li>
            <strong>Credits do not accrue interest</strong> and have no expiry date unless we
            publish a promotional expiry in advance.
          </li>
          <li>
            We may <strong>modify, expire or revoke</strong> credits, in whole or in part, to
            correct an error, reverse a payment that is reversed or charged back, or where
            credits were obtained through abuse of the referral programme. Where a change is
            material and affects you, we will tell you before it takes effect.
          </li>
          <li>
            The <strong>rupee amount you pay is the price of a service</strong>, not a deposit.
            Your credit balance is a record of promotional value granted to you.
          </li>
        </ul>

        <h6>3.2 Referral programme</h6>
        <p>
          Every account is issued a referral code and a shareable invite link. If someone signs
          up using your code, their account is attributed to you. A referral only becomes{' '}
          <em>qualified</em> — and only then do credits become available — once the invited
          account completes its first successful payment.
        </p>
        <p>
          This delay is deliberate: it prevents a stream of throwaway sign-ups from collecting
          credits without ever paying. Each referral is credited once. Rewards are granted as
          credits, not money, and are subject to everything in section 3.1.
        </p>
        <p>
          We record which account invited which other account, the code used, when the referral
          qualified, and the credits awarded. This is necessary to operate the programme, prevent
          fraud, and show you your own referral history.
        </p>
        <p>We do not build marketing profiles from referral data, and never sell it to anyone.</p>
      </>
    ),
  },
  {
    id: 'how-we-use-information',
    heading: '4. How we use your information',
    body: (
      <>
        <p>We use personal data only for these purposes:</p>
        <ul>
          <li>to create and authenticate your account and keep it secure;</li>
          <li>to connect your social accounts and publish, schedule and analyse content;</li>
          <li>to generate copy, images and video using our AI providers on your behalf;</li>
          <li>to process payments, issue credits, apply referral rewards and enforce plan limits;</li>
          <li>to detect, prevent and investigate fraud, abuse and security incidents;</li>
          <li>to send service messages — billing receipts, security alerts, plan changes;</li>
          <li>to improve the product in aggregate form that does not identify you;</li>
          <li>to comply with our legal and tax obligations.</li>
        </ul>
        <p>
          We do not sell your personal data. We do not use your content or connected accounts to
          train third-party models, and we do not share them for anyone else's purposes.
        </p>
      </>
    ),
  },
  {
    id: 'sharing',
    heading: '5. Who we share information with',
    body: (
      <>
        <p>We share the minimum necessary with:</p>
        <ul>
          <li>
            <strong>Social platforms</strong> (Meta, TikTok, LinkedIn, X, Google/YouTube) — to
            publish your content and retrieve your metrics, under their own privacy policies.
          </li>
          <li>
            <strong>Razorpay</strong> — to process your payment. It is an independent controller
            for card and UPI data.
          </li>
          <li>
            <strong>AI providers</strong> — if configured, prompts are sent to a model provider
            (for example Amazon Bedrock) to generate content. Your social credentials are never
            sent.
          </li>
          <li>
            <strong>Infrastructure providers</strong> — hosting, database, object storage, cache
            and queue providers, under confidentiality obligations.
          </li>
          <li>
            <strong>Professional advisers and authorities</strong> — where required by law, court
            order, or to establish or defend legal claims.
          </li>
        </ul>
        <p>
          If we transfer personal data outside your country, we do so under appropriate
          safeguards, such as standard contractual clauses.
        </p>
      </>
    ),
  },
  {
    id: 'cookies',
    heading: '6. Cookies and local storage',
    body: (
      <>
        <p>
          The web app stores a single item in your browser: a refresh token used to keep you
          signed in. We use no advertising cookies, cross-site trackers, or third-party analytics
          cookies.
        </p>
        <p>
          Our iOS and Android apps store the refresh token in the device's secure preference
          storage rather than browser storage.
        </p>
        <p>
          Blocking or deleting local storage signs you out, but your account and data are
          unaffected.
        </p>
      </>
    ),
  },
  {
    id: 'security',
    heading: '7. How we protect your information',
    body: (
      <>
        <ul>
          <li>Passwords are stored as salted bcrypt hashes and are never recoverable.</li>
          <li>Social access and refresh tokens are encrypted at rest using AES-256-GCM.</li>
          <li>Data is transmitted over TLS in transit.</li>
          <li>Administrative access is role-restricted and logged to an audit trail.</li>
          <li>Payment card and UPI data never reaches our servers.</li>
          <li>Payment confirmations are cryptographically verified server-side.</li>
        </ul>
        <p>
          No system is perfectly secure. If a breach affects your personal data and creates a
          legally notifiable risk, we will notify you and the relevant authority within the time
          required by law.
        </p>
      </>
    ),
  },
  {
    id: 'retention',
    heading: '8. How long we keep information',
    body: (
      <>
        <p>
          We keep personal data while your account is active, and afterwards for as long as we
          need it for the purposes it was collected — including resolving disputes, enforcing
          our terms, and meeting tax and accounting obligations.
        </p>
        <p>
          When we no longer need it we delete or anonymise it. Payment and credit records are
          retained for the period required by tax and anti-money-laundering law, even after an
          account is closed. Audit logs are retained for a limited period for security
          investigations.
        </p>
      </>
    ),
  },
  {
    id: 'your-rights',
    heading: '9. Your rights',
    body: (
      <>
        <p>Subject to applicable law, you have the right to:</p>
        <ul>
          <li><strong>Access</strong> — obtain a copy of the personal data we hold about you.</li>
          <li><strong>Correction</strong> — have inaccurate data corrected or completed.</li>
          <li><strong>Erasure</strong> — ask us to delete your data, where no retention obligation applies.</li>
          <li><strong>Withdrawal of consent</strong> — where we rely on consent, withdraw it.</li>
          <li><strong>Nomination</strong> — nominate someone to exercise your rights if you are unable.</li>
          <li><strong>Grievance redressal</strong> — complain to us and, if unresolved, to the relevant data protection authority.</li>
        </ul>
        <p>
          To exercise any of these, email{' '}
          <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. We verify identity before
          acting and respond within the period required by law. Note that deleting an account does
          not entitle you to any monetary payment, and credits are not money (see section 3.1).
        </p>
      </>
    ),
  },
  {
    id: 'children',
    heading: '10. Children',
    body: (
      <p>
        The service is not intended for anyone under 18. We do not knowingly collect data from
        children. If you believe a child has provided us data, contact us and we will delete it.
      </p>
    ),
  },
  {
    id: 'changes',
    heading: '11. Changes to this policy',
    body: (
      <>
        <p>
          We may update this policy to reflect changes in the product or the law. The "last
          updated" date at the top always reflects the current version. For material changes we
          will provide notice in the app or by email before they take effect.
        </p>
        <p>Continuing to use the service after a change means you accept the updated policy.</p>
      </>
    ),
  },
  {
    id: 'contact',
    heading: '12. Contact and complaints',
    body: (
      <p>
        For any privacy question, data request or grievance, contact our Grievance Officer at{' '}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. You also have the right to lodge
        a complaint with the relevant data protection authority in your jurisdiction.
      </p>
    ),
  },
];

export default function PrivacyPolicyPage() {
  return (
    <LegalDocument
      title="Privacy Policy"
      updated={UPDATED}
      intro="This policy explains what personal data Social Media Autopilot collects, why we collect it, who we share it with, and the rights you have over it. It applies to our web app, our iOS and Android apps, and our API."
      sections={SECTIONS}
    />
  );
}